import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import {
  CHATBOT_FEEDBACK_TYPES,
  CHATBOT_TEXT,
  CHATBOT_LOADING_WORDS,
  CHATBOT_CONFIG,
} from '@core/constants/chatbot.constants';
import { formatChatbotResponse } from '@core/helpers/common.helper';
import {
  ChatFeedbackType,
  ChatHistoryGroupedSection,
  ChatHistoryRecord,
  ChatbotMessage,
} from '@core/interfaces/chatbot.interface';
import { ChatbotService } from '@app/services/chatbot/chatbot.service';
import { hasQuillDangerousContent } from '@app/utils/common';
import { PROFILE_FORM_VALIDATION_MESSAGES } from '@app/core/constants/validation-messages.constants';
import { ALT_TEXT, IMAGE_PATHS } from '../../../core/constants/common.constant';
import { SafeHtmlPipe } from '@app/core/pipes/safe-html.pipe';

type ChatThreadItem =
  | { kind: 'date-separator'; id: string; label: string }
  | { kind: 'message'; id: string; message: ChatbotMessage };

@Component({
  selector: 'app-common-chat-button',
  imports: [CommonModule, SafeHtmlPipe],
  templateUrl: './common-chat-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommonChatButtonComponent {
  /** Scroll host used to keep latest message visible after updates. */
  @ViewChild('chatScrollContainer')
  private readonly chatScrollContainer?: ElementRef<HTMLDivElement>;
  /** Textarea ref used for lightweight auto-height growth while typing. */
  @ViewChild('chatMessageInput')
  private readonly chatMessageInput?: ElementRef<HTMLTextAreaElement>;

  readonly IMAGE_PATHS = IMAGE_PATHS;
  readonly ALT_TEXT = ALT_TEXT;
  readonly CHATBOT_TEXT = CHATBOT_TEXT;
  readonly CHATBOT_FEEDBACK_TYPES = CHATBOT_FEEDBACK_TYPES;
  readonly formatChatbotResponse = formatChatbotResponse;

  /** Controls chatbot panel visibility. */
  readonly isOpen = signal(false);
  /** Prevents duplicate sends and shows loading state while API calls are pending. */
  readonly isLoading = signal(false);
  /** Tracks grouped-history loading independently from live message posting. */
  readonly isHistoryLoading = signal(false);
  /** Two-way draft input state for current user message. */
  readonly draftMessage = signal('');
  readonly draftError = signal('');
  readonly VALIDATION = PROFILE_FORM_VALIDATION_MESSAGES;
  /** Single continuous chat thread containing past + current messages. */
  readonly messages = signal<ChatbotMessage[]>([]);
  /** Error banner text for grouped-history loading failures. */
  readonly historyError = signal('');
  /** Marks whether grouped history has already been loaded once in this UI lifecycle. */
  readonly isHistoryLoaded = signal(false);
  /** Tracks in-flight feedback submissions per bot message id. */
  readonly feedbackSubmittingByMessageId = signal<Record<string, boolean>>({});
  /** Short rotating status shown while bot response is being generated. */
  readonly loadingStatusWord = signal<string>(CHATBOT_TEXT.LOADING);
  /** Toggles floating action to jump directly to the latest message. */
  readonly showScrollToBottom = signal(false);

  /** Thread rendering model with WhatsApp-like centered day separators. */
  readonly threadItems = computed<ChatThreadItem[]>(() => {
    const items: ChatThreadItem[] = [];
    let lastDateLabel = '';

    for (const message of this.messages()) {
      const dateLabel = this.resolveDateLabel(message.createdAt);
      if (dateLabel !== lastDateLabel) {
        lastDateLabel = dateLabel;
        items.push({
          kind: 'date-separator',
          id: `date-${dateLabel}`,
          label: dateLabel,
        });
      }

      items.push({
        kind: 'message',
        id: `message-${message.id}`,
        message,
      });
    }

    return items;
  });

  private readonly chatbotService = inject(ChatbotService);
  private readonly router = inject(Router);
  /** Local monotonic suffix used when secure random uuid API is unavailable. */
  private messageSequence = 0;
  /** Display format used for message timestamps. */
  private readonly timeLabelOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };
  /** Candidate words shown while waiting for bot response. */
  private readonly loadingStatusWords = CHATBOT_LOADING_WORDS;

  /** Opens chatbot drawer. Background scroll remains enabled. */
  openChat(): void {
    this.isOpen.set(true);

    if (!this.isHistoryLoaded()) {
      this.loadGroupedHistory();
      return;
    }

    if (this.messages().length === 0) {
      this.appendMessage(this.createGreetingMessage());
    }

    this.scrollToBottom();
  }

  /** Closes chatbot drawer without clearing existing message state. */
  closeChat(): void {
    this.isOpen.set(false);
    this.showScrollToBottom.set(false);
  }

  /** Tracks whether chat scroll is away from the latest message and toggles quick-jump button. */
  onChatScroll(): void {
    const scrollContainer = this.chatScrollContainer?.nativeElement;
    if (!scrollContainer) {
      return;
    }

    const distanceFromBottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
    const significantScrollThreshold = Math.max(
      CHATBOT_CONFIG.SCROLL_THRESHOLD_PX,
      Math.floor(scrollContainer.clientHeight * CHATBOT_CONFIG.SCROLL_THRESHOLD_RATIO),
    );
    this.showScrollToBottom.set(distanceFromBottom > significantScrollThreshold);
  }

  /** Scrolls chat immediately to the latest message when user taps the quick-jump button. */
  scrollToLatest(): void {
    this.scrollToBottom();
  }

  /** Updates draft text from input events with safe element type guard. */
  onDraftChange(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement)) {
      return;
    }

    this.draftMessage.set(target.value);
    this.draftError.set(hasQuillDangerousContent(target.value) ? this.VALIDATION.javascript : '');
    this.autoResizeDraftInput(target);
  }

  /** Sends on Enter; allows Shift+Enter for new line in textarea. */
  onDraftKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    this.sendMessage();
  }

  /** Routes in-app links from chatbot messages without triggering a full page refresh. */
  onMessageBodyClick(event: MouseEvent): void {
    this.handleMessageBodyLinkActivation(event);
  }

  /** Supports keyboard activation for links rendered inside chatbot message bodies. */
  onMessageBodyKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.handleMessageBodyLinkActivation(event);
  }

  /** Sends user message, appends optimistic local entry, then appends bot response/fallback. */
  sendMessage(): void {
    const message = this.draftMessage().trim();
    if (!message || this.isLoading() || hasQuillDangerousContent(message)) {
      return;
    }

    const createdAt = new Date().toISOString();
    const userMessage: ChatbotMessage = {
      id: this.buildMessageId('user'),
      text: message,
      sender: 'user',
      timestamp: this.getCurrentTimeLabel(),
      createdAt,
    };

    this.appendMessage(userMessage);
    this.draftMessage.set('');
    this.draftError.set('');
    this.autoResizeDraftInput();
    this.loadingStatusWord.set(this.pickRandomLoadingStatusWord());
    this.isLoading.set(true);

    this.chatbotService
      .sendMessage(message)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (botMessage) => {
          this.appendMessage({
            ...botMessage,
            question: message,
            createdAt: new Date().toISOString(),
          });
        },
        error: () => {
          this.appendMessage(this.createFallbackBotMessage(message));
        },
      });
  }

  /** Submits optional thumbs up/down feedback for a bot response. */
  submitFeedback(message: ChatbotMessage, feedbackType: ChatFeedbackType): void {
    // Look up the current message from the signal to check the latest feedback state
    const currentMessage = this.messages().find((m) => m.id === message.id);

    if (
      message.sender !== 'bot' ||
      !message.question ||
      currentMessage?.feedbackType === feedbackType ||
      this.isFeedbackSubmitting(message.id)
    ) {
      return;
    }

    this.setFeedbackSubmitting(message.id, true);
    this.chatbotService
      .submitFeedback(message.question, message.text, feedbackType)
      .pipe(finalize(() => this.setFeedbackSubmitting(message.id, false)))
      .subscribe({
        next: () => {
          this.updateMessageFeedbackType(message.id, feedbackType);
        },
        error: () => {
          // Feedback is optional; ignore failures to avoid interrupting chat usage.
        },
      });
  }

  /** Returns whether feedback controls should be rendered for a bot message. */
  canShowFeedback(message: ChatbotMessage): boolean {
    return (
      message.sender === 'bot' &&
      !!message.question &&
      !message.id.startsWith(CHATBOT_TEXT.HISTORY_BOT_ID_PREFIX)
    );
  }

  /** Returns whether a feedback request is currently in-flight for one message. */
  isFeedbackSubmitting(messageId: string): boolean {
    return this.feedbackSubmittingByMessageId()[messageId] ?? false;
  }

  /** Renders timestamp only at the end of each sender streak to reduce clutter. */
  shouldShowTimestamp(threadItemIndex: number): boolean {
    const thread = this.threadItems();
    const currentItem = thread[threadItemIndex];
    const nextItem = thread[threadItemIndex + 1];

    if (currentItem?.kind !== 'message') {
      return false;
    }

    if (nextItem?.kind !== 'message') {
      return true;
    }

    return nextItem.message.sender !== currentItem.message.sender;
  }

  private handleMessageBodyLinkActivation(event: MouseEvent | KeyboardEvent): void {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const anchor = target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) {
      return;
    }

    const rawHref = anchor.getAttribute('href')?.trim() ?? '';
    if (!rawHref || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) {
      return;
    }

    const internalRoute = this.toInternalRoute(rawHref);
    if (!internalRoute) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    void this.router.navigateByUrl(internalRoute);
    if (globalThis.matchMedia('(max-width: 767px)').matches) {
      this.closeChat();
    }
  }

  /** Auto-expands composer up to four lines, then keeps internal scrollbar. */
  private autoResizeDraftInput(input?: HTMLTextAreaElement): void {
    const textarea = input ?? this.chatMessageInput?.nativeElement;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    const maxHeight = CHATBOT_CONFIG.MAX_TEXTAREA_HEIGHT_PX;
    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  /** Creates a local assistant fallback reply when API response is unavailable. */
  private createFallbackBotMessage(question: string): ChatbotMessage {
    const createdAt = new Date().toISOString();
    return {
      id: this.buildMessageId('bot'),
      text: CHATBOT_TEXT.FALLBACK_BOT_REPLY,
      sender: 'bot',
      timestamp: this.getCurrentTimeLabel(),
      question,
      createdAt,
    };
  }

  /** Creates the default assistant greeting shown when no history exists. */
  private createGreetingMessage(): ChatbotMessage {
    const createdAt = new Date().toISOString();
    return {
      id: this.buildMessageId('bot'),
      text: CHATBOT_TEXT.DEFAULT_BOT_GREETING,
      sender: 'bot',
      timestamp: this.getCurrentTimeLabel(),
      createdAt,
    };
  }

  /** Loads grouped per-user history and hydrates one continuous thread. */
  private loadGroupedHistory(): void {
    this.isHistoryLoading.set(true);
    this.historyError.set('');

    this.chatbotService
      .getGroupedHistoryByUser()
      .pipe(finalize(() => this.isHistoryLoading.set(false)))
      .subscribe({
        next: (sections) => {
          this.isHistoryLoaded.set(true);
          const historicalMessages = this.mapGroupedHistoryToMessages(sections);
          this.messages.set(historicalMessages);

          if (historicalMessages.length === 0) {
            this.appendMessage(this.createGreetingMessage());
          } else {
            this.scrollToBottom();
          }
        },
        error: () => {
          this.isHistoryLoaded.set(true);
          this.historyError.set(CHATBOT_TEXT.HISTORY_ERROR);
          if (this.messages().length === 0) {
            this.appendMessage(this.createGreetingMessage());
          }
        },
      });
  }

  /** Flattens grouped history into one chronological sequence of user and bot bubbles. */
  private mapGroupedHistoryToMessages(sections: ChatHistoryGroupedSection[]): ChatbotMessage[] {
    const sortableRows = sections
      .flatMap((section) =>
        (section.messages ?? []).map((record) => ({
          record,
          timestamp: this.toTimestamp(record.created_at),
        })),
      )
      .sort((left, right) => left.timestamp - right.timestamp)
      .map((entry) => entry.record);

    return sortableRows.flatMap((record) => this.mapHistoryRecordToMessages(record));
  }

  /** Normalizes one history record into two chat bubbles: user question and bot answer. */
  private mapHistoryRecordToMessages(record: ChatHistoryRecord): ChatbotMessage[] {
    const timestamp = this.getTimeLabelFromValue(record.created_at);
    const createdAt = this.normalizeHistoryTimestamp(record.created_at);
    const messages: ChatbotMessage[] = [];

    if (record.question?.trim()) {
      messages.push({
        id: `${CHATBOT_TEXT.HISTORY_USER_ID_PREFIX}${record.id}`,
        text: record.question,
        sender: 'user',
        timestamp,
        createdAt,
      });
    }

    if (record.answer?.trim()) {
      messages.push({
        id: `${CHATBOT_TEXT.HISTORY_BOT_ID_PREFIX}${record.id}`,
        text: record.answer,
        sender: 'bot',
        timestamp,
        createdAt,
      });
    }

    return messages;
  }

  /** Generates lightweight client-side message ids for optimistic rendering. */
  private buildMessageId(sender: 'bot' | 'user'): string {
    const secureId = globalThis.crypto?.randomUUID?.();
    if (secureId) {
      return `${sender}-${secureId}`;
    }

    this.messageSequence += 1;
    return `${sender}-${Date.now()}-${this.messageSequence}`;
  }

  /** Formats current local time for chat bubble metadata. */
  private getCurrentTimeLabel(): string {
    return new Date().toLocaleTimeString([], this.timeLabelOptions);
  }

  /** Picks one random loading label for the current request lifecycle. */
  private pickRandomLoadingStatusWord(): string {
    const randomValues = new Uint32Array(1);
    crypto.getRandomValues(randomValues);
    const index = randomValues[0] % this.loadingStatusWords.length;
    return this.loadingStatusWords[index];
  }

  /** Formats a history row timestamp to match the live chat bubble time label. */
  private getTimeLabelFromValue(value: string | undefined): string {
    const timestamp = this.toTimestamp(value);
    if (timestamp === 0) {
      return this.getCurrentTimeLabel();
    }

    return new Date(timestamp).toLocaleTimeString([], this.timeLabelOptions);
  }

  /** Resolves a centered separator label in WhatsApp-style (Today, Yesterday, or full date). */
  private resolveDateLabel(value: string | undefined): string {
    const timestamp = this.toTimestamp(value);
    const messageDate = timestamp === 0 ? new Date() : new Date(timestamp);

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfMessageDay = new Date(
      messageDate.getFullYear(),
      messageDate.getMonth(),
      messageDate.getDate(),
    );

    const dayMs = CHATBOT_CONFIG.DAY_MS;
    const dayDiff = Math.round((startOfToday.getTime() - startOfMessageDay.getTime()) / dayMs);

    if (dayDiff === 0) {
      return CHATBOT_TEXT.DATE_TODAY;
    }

    if (dayDiff === 1) {
      return CHATBOT_TEXT.DATE_YESTERDAY;
    }

    return messageDate.toLocaleDateString(CHATBOT_TEXT.DATE_LOCALE, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  /** Parses an ISO-like timestamp into epoch milliseconds, returning 0 when invalid. */
  private toTimestamp(value: string | undefined): number {
    if (!value) {
      return 0;
    }

    const normalizedValue = this.normalizeHistoryTimestamp(value);
    const timestamp = new Date(normalizedValue).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  /**
   * Backend can return timezone-naive values (without Z/offset).
   * Treat such values as UTC so labels match local UI conversion.
   */
  private normalizeHistoryTimestamp(value: string): string {
    const hasTimezoneSuffix = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    if (hasTimezoneSuffix) {
      return value;
    }

    return `${value}Z`;
  }

  /** Appends a message to thread and auto-scrolls to the latest item. */
  private appendMessage(message: ChatbotMessage): void {
    this.messages.update((current) => [...current, message]);
    this.scrollToBottom();
  }

  /** Marks a bot message with the latest selected feedback type. */
  private updateMessageFeedbackType(messageId: string, feedbackType: ChatFeedbackType): void {
    this.messages.update((current) =>
      current.map((message) => (message.id === messageId ? { ...message, feedbackType } : message)),
    );
  }

  /** Tracks feedback loading state per message for button disable handling. */
  private setFeedbackSubmitting(messageId: string, isSubmitting: boolean): void {
    this.feedbackSubmittingByMessageId.update((current) => {
      if (isSubmitting) {
        return {
          ...current,
          [messageId]: true,
        };
      }

      return Object.fromEntries(Object.entries(current).filter(([id]) => id !== messageId));
    });
  }

  /** Maps a href to an internal route when it belongs to this app. */
  private toInternalRoute(href: string): string | null {
    if (href.startsWith('/')) {
      return href;
    }

    if (!/^(?:https?:)?\/\//i.test(href)) {
      return null;
    }

    try {
      const parsed = new URL(href, window.location.origin);
      if (parsed.origin !== window.location.origin) {
        return null;
      }

      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return null;
    }
  }

  /** Defers scroll update until DOM has rendered the latest message list changes. */
  private scrollToBottom(): void {
    setTimeout(() => {
      const scrollContainer = this.chatScrollContainer?.nativeElement;
      if (!scrollContainer) {
        return;
      }

      scrollContainer.scrollTop = scrollContainer.scrollHeight;
      this.showScrollToBottom.set(false);
    }, 0);
  }
}
